import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft, CheckCircle2, ChevronRight, Download, File as FileIcon,
  FileUp, Files, FolderUp, LoaderCircle, LogOut, RotateCcw, Trash2,
  UploadCloud, XCircle,
} from 'lucide-react';
import {
  completeUploadAnythingBatch,
  createUploadAnythingBatch,
  deleteUploadAnythingBatch,
  deleteUploadAnythingFile,
  deleteUploadAnythingFiles,
  downloadUploadAnythingFile,
  listUploadAnythingBatches,
  listUploadAnythingFiles,
  uploadAnythingFile,
} from '../../services/api.js';
import {
  captureDrop,
  formatBytes,
  formatEta,
  formatTransferRate,
  scanCapturedDrop,
  summarizeUploadItems,
  updateFileSelection,
  uploadItem,
  validateUploadItems,
} from './uploadAnythingFiles.js';
import styles from './UploadAnythingPage.module.css';

const CONCURRENCY = 3;

function formatUploadDate(value) {
  if (!value) return 'Upload';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Upload';
  return date.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
}

export default function UploadAnythingPage({ currentUser, onLogout }) {
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const rowsRef = useRef([]);
  const abortRef = useRef(null);
  const selectionAnchorRef = useRef({});
  const progressSampleRef = useRef({ at: 0, bytes: 0, speed: 0 });

  const [phase, setPhase] = useState('idle');
  const [dragging, setDragging] = useState(false);
  const [scanCount, setScanCount] = useState(0);
  const [items, setItems] = useState([]);
  const [rows, setRows] = useState([]);
  const [batch, setBatch] = useState(null);
  const [message, setMessage] = useState('');
  const [uploads, setUploads] = useState([]);
  const [uploadsLoading, setUploadsLoading] = useState(true);
  const [uploadsError, setUploadsError] = useState('');
  const [expandedBatchId, setExpandedBatchId] = useState(null);
  const [filesByBatch, setFilesByBatch] = useState({});
  const [fileErrors, setFileErrors] = useState({});
  const [downloading, setDownloading] = useState('');
  const [deleting, setDeleting] = useState('');
  const [selectedByBatch, setSelectedByBatch] = useState({});
  const [transferSpeed, setTransferSpeed] = useState(0);

  const refreshUploads = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setUploadsLoading(true);
    try {
      const result = await listUploadAnythingBatches();
      setUploads(result.batches || []);
      setUploadsError('');
    } catch (error) {
      setUploadsError(error.message || 'Could not load your uploads.');
    } finally {
      if (!silent) setUploadsLoading(false);
    }
  }, []);

  useEffect(() => {
    const input = folderInputRef.current;
    if (!input) return;
    input.setAttribute('webkitdirectory', '');
    input.setAttribute('directory', '');
  }, []);

  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => {
    refreshUploads();
  }, [refreshUploads]);

  const summary = useMemo(() => summarizeUploadItems(items), [items]);
  const transferred = rows.reduce((sum, row) => (
    sum + (row.status === 'done' ? row.file.size : row.loaded || 0)
  ), 0);
  const doneCount = rows.filter(row => row.status === 'done').length;
  const progress = summary.totalBytes > 0
    ? Math.min(100, Math.round((transferred / summary.totalBytes) * 100))
    : (rows.length ? Math.round((doneCount / rows.length) * 100) : 0);
  const failedRows = rows.filter(row => row.status === 'error' || row.status === 'canceled');
  const activeRows = rows.filter(row => row.status === 'uploading').slice(0, CONCURRENCY);
  const etaSeconds = transferSpeed > 0
    ? Math.max(0, summary.totalBytes - transferred) / transferSpeed
    : 0;

  useEffect(() => {
    if (phase !== 'uploading') return;
    const now = Date.now();
    const sample = progressSampleRef.current;
    if (!sample.at) {
      progressSampleRef.current = { at: now, bytes: transferred, speed: 0 };
      return;
    }
    const elapsedSeconds = (now - sample.at) / 1000;
    if (elapsedSeconds < 0.25) return;
    const byteDelta = transferred - sample.bytes;
    if (byteDelta < 0) {
      progressSampleRef.current = { at: now, bytes: transferred, speed: 0 };
      setTransferSpeed(0);
      return;
    }
    const instantSpeed = byteDelta / elapsedSeconds;
    const smoothedSpeed = sample.speed > 0
      ? (sample.speed * 0.72) + (instantSpeed * 0.28)
      : instantSpeed;
    progressSampleRef.current = {
      at: now,
      bytes: transferred,
      speed: smoothedSpeed,
    };
    setTransferSpeed(smoothedSpeed);
  }, [phase, transferred]);

  const replaceRows = (next) => {
    rowsRef.current = next;
    setRows([...next]);
  };
  const updateRow = (index, updates) => {
    rowsRef.current[index] = { ...rowsRef.current[index], ...updates };
    setRows([...rowsRef.current]);
  };

  const acceptItems = (nextItems) => {
    const errors = validateUploadItems(nextItems);
    if (errors.length) {
      setMessage(errors.slice(0, 4).join(' '));
      setPhase('idle');
      return;
    }
    setItems(nextItems);
    setRows([]);
    rowsRef.current = [];
    setBatch(null);
    setMessage('');
    setPhase('ready');
  };

  const scanDrop = async (captured) => {
    setPhase('scanning');
    setScanCount(0);
    setMessage('');
    try {
      acceptItems(await scanCapturedDrop(captured, setScanCount));
    } catch (error) {
      setPhase('idle');
      setMessage(error.message || 'Could not scan that selection.');
    }
  };

  const finalize = async (batchInfo) => {
    setPhase('finalizing');
    try {
      const completed = await completeUploadAnythingBatch(batchInfo.batch_id);
      setBatch(completed);
      setMessage('');
      setPhase('complete');
      void refreshUploads({ silent: true });
    } catch (error) {
      setMessage(error.message || 'All files arrived, but the batch could not be finalized.');
      setPhase('partial');
    }
  };

  const transfer = async (batchInfo, retryOnly = false) => {
    const controller = new AbortController();
    abortRef.current = controller;
    setMessage('');
    setPhase('uploading');

    const nextRows = retryOnly
      ? rowsRef.current.map(row => (
          row.status === 'error' || row.status === 'canceled'
            ? { ...row, status: 'queued', loaded: 0, error: '' }
            : row
        ))
      : items.map((item, index) => ({
          ...item, id: index, status: 'queued', loaded: 0, error: '',
        }));
    const startingBytes = nextRows.reduce((sum, row) => (
      sum + (row.status === 'done' ? row.file.size : 0)
    ), 0);
    progressSampleRef.current = { at: Date.now(), bytes: startingBytes, speed: 0 };
    setTransferSpeed(0);
    replaceRows(nextRows);
    let cursor = 0;

    const worker = async () => {
      while (!controller.signal.aborted) {
        while (cursor < rowsRef.current.length
               && rowsRef.current[cursor].status !== 'queued') cursor += 1;
        if (cursor >= rowsRef.current.length) return;
        const index = cursor;
        cursor += 1;
        const row = rowsRef.current[index];
        updateRow(index, {
          status: 'uploading', loaded: 0, error: '', retryMessage: '',
        });
        try {
          await uploadAnythingFile(
            batchInfo.batch_id,
            row.path,
            row.file,
            loaded => updateRow(index, { loaded, retryMessage: '' }),
            controller.signal,
            {
              onRetry: ({ attempt, maxAttempts, reason }) => updateRow(index, {
                loaded: 0,
                retryMessage: `${reason}. Retrying attempt ${attempt} of ${maxAttempts}…`,
              }),
            },
          );
          updateRow(index, {
            status: 'done', loaded: row.file.size, retryMessage: '',
          });
        } catch (error) {
          updateRow(index, {
            status: error.name === 'AbortError' ? 'canceled' : 'error',
            error: error.name === 'AbortError' ? 'Canceled' : (error.message || 'Upload failed'),
          });
        }
      }
    };

    await Promise.all(Array.from({ length: CONCURRENCY }, worker));
    abortRef.current = null;
    if (controller.signal.aborted) {
      replaceRows(rowsRef.current.map(row => (
        row.status === 'queued' ? { ...row, status: 'canceled', error: 'Canceled' } : row
      )));
    }
    const allDone = rowsRef.current.length > 0
      && rowsRef.current.every(row => row.status === 'done');
    if (allDone) await finalize(batchInfo);
    else setPhase('partial');
  };

  const startUpload = async () => {
    setMessage('');
    try {
      const created = await createUploadAnythingBatch(summary.fileCount, summary.totalBytes);
      setBatch(created);
      await transfer(created);
    } catch (error) {
      setMessage(error.message || 'Could not start the upload.');
      setPhase('ready');
    }
  };

  const retry = async () => {
    if (!batch) return;
    if (rowsRef.current.every(row => row.status === 'done')) await finalize(batch);
    else await transfer(batch, true);
  };

  const reset = () => {
    setItems([]);
    replaceRows([]);
    setBatch(null);
    setMessage('');
    setPhase('idle');
  };

  const toggleBatch = async (batchId) => {
    if (expandedBatchId === batchId) {
      setExpandedBatchId(null);
      return;
    }
    setExpandedBatchId(batchId);
    if (Object.prototype.hasOwnProperty.call(filesByBatch, batchId)) return;
    setFileErrors(current => ({ ...current, [batchId]: '' }));
    try {
      const result = await listUploadAnythingFiles(batchId);
      setFilesByBatch(current => ({ ...current, [batchId]: result.files || [] }));
    } catch (error) {
      setFileErrors(current => ({
        ...current,
        [batchId]: error.message || 'Could not load uploaded files.',
      }));
    }
  };

  const downloadFile = async (batchId, file) => {
    const key = `${batchId}:${file.path}`;
    setDownloading(key);
    setUploadsError('');
    try {
      await downloadUploadAnythingFile(batchId, file.path);
    } catch (error) {
      setUploadsError(error.message || 'Could not download this file.');
    } finally {
      setDownloading('');
    }
  };

  const deleteFile = async (upload, file) => {
    if (!window.confirm(`Delete “${file.path}”? This cannot be undone.`)) return;
    const key = `file:${upload.batch_id}:${file.path}`;
    setDeleting(key);
    setUploadsError('');
    try {
      const updated = await deleteUploadAnythingFile(upload.batch_id, file.path);
      setUploads(current => current.map(item => (
        item.batch_id === upload.batch_id ? { ...item, ...updated } : item
      )));
      setFilesByBatch(current => ({
        ...current,
        [upload.batch_id]: (current[upload.batch_id] || [])
          .filter(item => item.path !== file.path),
      }));
      setSelectedByBatch(current => ({
        ...current,
        [upload.batch_id]: (current[upload.batch_id] || [])
          .filter(path => path !== file.path),
      }));
      if (selectionAnchorRef.current[upload.batch_id] === file.path) {
        delete selectionAnchorRef.current[upload.batch_id];
      }
    } catch (error) {
      setUploadsError(error.message || 'Could not delete this file.');
    } finally {
      setDeleting('');
    }
  };

  const selectFile = (batchId, file, checked, shiftKey) => {
    const orderedPaths = (filesByBatch[batchId] || []).map(item => item.path);
    setSelectedByBatch(current => {
      const result = updateFileSelection(
        current[batchId] || [],
        orderedPaths,
        file.path,
        {
          checked,
          shiftKey,
          anchorPath: selectionAnchorRef.current[batchId] || null,
        },
      );
      selectionAnchorRef.current[batchId] = result.anchorPath;
      return { ...current, [batchId]: result.selectedPaths };
    });
  };

  const deleteSelectedFiles = async (upload) => {
    const selectedPaths = selectedByBatch[upload.batch_id] || [];
    if (!selectedPaths.length) return;
    const fileLabel = `${selectedPaths.length.toLocaleString()} selected file${selectedPaths.length === 1 ? '' : 's'}`;
    if (!window.confirm(`Delete ${fileLabel}? This cannot be undone.`)) return;
    const key = `bulk:${upload.batch_id}`;
    setDeleting(key);
    setUploadsError('');
    try {
      const updated = await deleteUploadAnythingFiles(upload.batch_id, selectedPaths);
      const deletedPaths = new Set(updated.deleted_paths || selectedPaths);
      setUploads(current => current.map(item => (
        item.batch_id === upload.batch_id ? { ...item, ...updated } : item
      )));
      setFilesByBatch(current => ({
        ...current,
        [upload.batch_id]: (current[upload.batch_id] || [])
          .filter(item => !deletedPaths.has(item.path)),
      }));
      setSelectedByBatch(current => ({ ...current, [upload.batch_id]: [] }));
      delete selectionAnchorRef.current[upload.batch_id];
    } catch (error) {
      setUploadsError(error.message || 'Could not delete selected files.');
    } finally {
      setDeleting('');
    }
  };

  const deleteWholeUpload = async (upload) => {
    const fileLabel = `${upload.file_count.toLocaleString()} file${upload.file_count === 1 ? '' : 's'}`;
    if (!window.confirm(`Delete this upload and all ${fileLabel}? This cannot be undone.`)) return;
    const key = `batch:${upload.batch_id}`;
    setDeleting(key);
    setUploadsError('');
    try {
      await deleteUploadAnythingBatch(upload.batch_id);
      setUploads(current => current.filter(item => item.batch_id !== upload.batch_id));
      setFilesByBatch(current => {
        const next = { ...current };
        delete next[upload.batch_id];
        return next;
      });
      setSelectedByBatch(current => {
        const next = { ...current };
        delete next[upload.batch_id];
        return next;
      });
      delete selectionAnchorRef.current[upload.batch_id];
      setExpandedBatchId(current => current === upload.batch_id ? null : current);
    } catch (error) {
      setUploadsError(error.message || 'Could not delete this upload.');
    } finally {
      setDeleting('');
    }
  };

  const selectable = ['idle', 'ready', 'complete'].includes(phase);

  return (
    <main className={styles.page}>
      <header className={styles.header}>
        <a href="/" className={styles.back}><ArrowLeft size={16} /> Track Day Analysis</a>
        <div className={styles.account}>
          <span>{currentUser?.display_name || currentUser?.email}</span>
          {onLogout && (
            <button type="button" onClick={onLogout}><LogOut size={15} /> Sign out</button>
          )}
        </div>
      </header>

      <section className={styles.card}>
        <div className={styles.intro}>
          <span className={styles.eyebrow}>FILE TRANSFER</span>
          <h1>Upload anything</h1>
          <p>Select files or a directory. If you choose a directory, it will upload all folders within it recursively. Happy uploading!</p>
        </div>

        <div
          className={`${styles.dropzone} ${dragging ? styles.dragging : ''} ${!selectable ? styles.disabled : ''}`}
          onDragOver={event => { event.preventDefault(); if (selectable) setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={event => {
            event.preventDefault();
            setDragging(false);
            if (!selectable) return;
            const captured = captureDrop(event.dataTransfer);
            scanDrop(captured);
          }}
        >
          <UploadCloud size={42} strokeWidth={1.4} />
          <p className={styles.dropTitle}>
            {phase === 'scanning' ? `Scanning… ${scanCount} files found` : 'Drop files or folders here'}
          </p>
          <p className={styles.dropHint}>or choose what you want to send</p>
          <div className={styles.pickers}>
            <button type="button" disabled={!selectable} onClick={() => fileInputRef.current?.click()}>
              <FileUp size={17} /> Choose files
            </button>
            <button type="button" disabled={!selectable} onClick={() => folderInputRef.current?.click()}>
              <FolderUp size={17} /> Choose a folder
            </button>
          </div>
        </div>

        <input
          ref={fileInputRef} type="file" multiple hidden
          onChange={event => {
            const selected = Array.from(event.target.files || []).map(file => uploadItem(file));
            event.target.value = '';
            if (selected.length) acceptItems(selected);
          }}
        />
        <input
          ref={folderInputRef} type="file" multiple hidden
          onChange={event => {
            const selected = Array.from(event.target.files || []).map(file => uploadItem(file));
            event.target.value = '';
            if (selected.length) acceptItems(selected);
          }}
        />

        {message && <div className={styles.error}><XCircle size={17} /> {message}</div>}

        {phase === 'ready' && (
          <div className={styles.review}>
            <div><strong>{summary.fileCount.toLocaleString()}</strong><span>files</span></div>
            <div><strong>{formatBytes(summary.totalBytes)}</strong><span>total size</span></div>
            <button type="button" className={styles.primary} onClick={startUpload}>Upload everything</button>
          </div>
        )}

        {['uploading', 'partial', 'finalizing'].includes(phase) && (
          <div className={styles.transfer}>
            <div className={styles.transferTop}>
              <div>
                <strong>{phase === 'finalizing' ? 'Verifying files…' : `${doneCount} of ${rows.length} files`}</strong>
                <span>
                  {formatBytes(transferred)} of {formatBytes(summary.totalBytes)}
                  {phase === 'uploading' && transferSpeed > 0
                    ? ` · ${formatTransferRate(transferSpeed)}${etaSeconds > 0 ? ` · ${formatEta(etaSeconds)}` : ''}`
                    : ''}
                </span>
              </div>
              <div className={styles.progressReadout}>
                <span className={styles.percent}>{progress}%</span>
                <small>{phase === 'finalizing' ? 'CHECKING' : 'UPLOADING'}</small>
              </div>
            </div>
            <div className={`${styles.track} ${phase === 'finalizing' ? styles.trackFinalizing : styles.trackActive}`}>
              <span style={{ width: `${progress}%` }} />
            </div>
            {phase === 'uploading' && activeRows.length > 0 && (
              <div className={styles.activeTransfers}>
                {activeRows.map(row => (
                  <span key={row.id} title={row.path}>
                    <UploadCloud size={13} /><em>{row.path}</em>
                    {row.retryMessage && <small>{row.retryMessage}</small>}
                  </span>
                ))}
              </div>
            )}
            {batch && <code className={styles.batchId}>{batch.batch_id}</code>}
            {failedRows.length > 0 && (
              <div className={styles.failures}>
                {failedRows.slice(0, 12).map(row => (
                  <div key={row.id}><span>{row.path}</span><small>{row.error}</small></div>
                ))}
                {failedRows.length > 12 && <p>And {failedRows.length - 12} more failed files.</p>}
              </div>
            )}
            <div className={styles.actions}>
              {phase === 'uploading' && (
                <button type="button" className={styles.secondary} onClick={() => abortRef.current?.abort()}>Cancel</button>
              )}
              {phase === 'partial' && (
                <button type="button" className={styles.primary} onClick={retry}>
                  <RotateCcw size={16} /> {failedRows.length ? 'Retry failed files' : 'Try finalizing again'}
                </button>
              )}
            </div>
          </div>
        )}

        {phase === 'complete' && batch && (
          <div className={styles.success}>
            <CheckCircle2 size={34} />
            <div>
              <h2>Upload complete</h2>
              <p>{batch.file_count.toLocaleString()} files · {formatBytes(batch.total_bytes)}</p>
              <div className={styles.completeTrack}><span /></div>
              <code>{batch.destination}</code>
            </div>
            <button type="button" className={styles.secondary} onClick={reset}>Start another upload</button>
          </div>
        )}
      </section>

      <section className={`${styles.card} ${styles.library}`}>
        <div className={styles.libraryHeader}>
          <div>
            <span className={styles.eyebrow}>YOUR FILES</span>
            <h2>Previous uploads</h2>
            <p>Browse and download files you have uploaded.</p>
          </div>
          <Files size={28} strokeWidth={1.5} />
        </div>

        {uploadsError && <div className={styles.error}><XCircle size={17} /> {uploadsError}</div>}

        {uploadsLoading ? (
          <div className={styles.libraryState}><LoaderCircle className={styles.spin} size={20} /> Loading your uploads…</div>
        ) : uploads.length === 0 ? (
          <div className={styles.libraryState}>Your completed uploads will appear here.</div>
        ) : (
          <div className={styles.uploadList}>
            {uploads.map(upload => {
              const expanded = expandedBatchId === upload.batch_id;
              const fileList = filesByBatch[upload.batch_id];
              const fileError = fileErrors[upload.batch_id];
              const selectedPaths = selectedByBatch[upload.batch_id] || [];
              const selectedSet = new Set(selectedPaths);
              const batchDeleteKey = `batch:${upload.batch_id}`;
              const bulkDeleteKey = `bulk:${upload.batch_id}`;
              return (
                <div className={styles.uploadBatch} key={upload.batch_id}>
                  <div className={styles.batchHeader}>
                    <button
                      type="button"
                      className={styles.batchSummary}
                      aria-expanded={expanded}
                      onClick={() => toggleBatch(upload.batch_id)}
                    >
                      <ChevronRight className={expanded ? styles.expanded : ''} size={19} />
                      <span className={styles.batchTitle}>
                        <strong>{formatUploadDate(upload.completed_at || upload.created_at)}</strong>
                        <small>{upload.file_count.toLocaleString()} files · {formatBytes(upload.total_bytes)}</small>
                      </span>
                      <code>{upload.batch_id}</code>
                    </button>
                    <button
                      type="button"
                      className={styles.batchDelete}
                      disabled={Boolean(deleting || downloading)}
                      onClick={() => deleteWholeUpload(upload)}
                      aria-label={`Delete upload from ${formatUploadDate(upload.completed_at || upload.created_at)}`}
                    >
                      {deleting === batchDeleteKey
                        ? <LoaderCircle className={styles.spin} size={16} />
                        : <Trash2 size={16} />}
                      Delete
                    </button>
                  </div>

                  {expanded && (
                    <div className={styles.fileList}>
                      {fileError ? (
                        <div className={styles.fileState}>{fileError}</div>
                      ) : !fileList ? (
                        <div className={styles.fileState}><LoaderCircle className={styles.spin} size={17} /> Loading files…</div>
                      ) : fileList.length === 0 ? (
                        <div className={styles.fileState}>No files found in this upload.</div>
                      ) : <>
                        <div className={styles.fileToolbar}>
                          <span>
                            {selectedPaths.length
                              ? `${selectedPaths.length.toLocaleString()} selected`
                              : 'Select a file, then Shift-click another to select a range.'}
                          </span>
                          {selectedPaths.length > 0 && (
                            <div className={styles.selectionActions}>
                              <button
                                type="button"
                                disabled={Boolean(deleting || downloading)}
                                onClick={() => {
                                  setSelectedByBatch(current => ({
                                    ...current, [upload.batch_id]: [],
                                  }));
                                  delete selectionAnchorRef.current[upload.batch_id];
                                }}
                              >
                                Clear
                              </button>
                              <button
                                type="button"
                                className={styles.deleteButton}
                                disabled={Boolean(deleting || downloading)}
                                onClick={() => deleteSelectedFiles(upload)}
                              >
                                {deleting === bulkDeleteKey
                                  ? <LoaderCircle className={styles.spin} size={16} />
                                  : <Trash2 size={16} />}
                                Delete selected
                              </button>
                            </div>
                          )}
                        </div>
                        {fileList.map(file => {
                          const key = `${upload.batch_id}:${file.path}`;
                          const deleteKey = `file:${upload.batch_id}:${file.path}`;
                          const selected = selectedSet.has(file.path);
                          return (
                            <div
                              className={`${styles.fileRow} ${selected ? styles.fileSelected : ''}`}
                              key={file.path}
                            >
                              <input
                                className={styles.fileSelect}
                                type="checkbox"
                                checked={selected}
                                onChange={event => selectFile(
                                  upload.batch_id,
                                  file,
                                  event.target.checked,
                                  event.nativeEvent.shiftKey,
                                )}
                                aria-label={`Select ${file.path}`}
                              />
                              <FileIcon size={17} />
                              <span title={file.path}>{file.path}</span>
                              <small>{formatBytes(file.bytes)}</small>
                              <div className={styles.fileActions}>
                                <button
                                  type="button"
                                  disabled={downloading === key || Boolean(deleting)}
                                  onClick={() => downloadFile(upload.batch_id, file)}
                                  aria-label={`Download ${file.path}`}
                                >
                                  {downloading === key
                                    ? <LoaderCircle className={styles.spin} size={16} />
                                    : <Download size={16} />}
                                  Download
                                </button>
                                <button
                                  type="button"
                                  className={styles.deleteButton}
                                  disabled={Boolean(deleting || downloading)}
                                  onClick={() => deleteFile(upload, file)}
                                  aria-label={`Delete ${file.path}`}
                                >
                                  {deleting === deleteKey
                                    ? <LoaderCircle className={styles.spin} size={16} />
                                    : <Trash2 size={16} />}
                                  Delete
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
}
